import PropTypes from 'prop-types'
import { Button, DatePicker, Popconfirm, Tooltip } from 'antd'
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DeleteFilled,
  EditFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { formatDOB } from '../../lib/datetime'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import DiseasesTags from '../Common/DiseasesTags'
import PatientEntry from '../Common/PatientEntry'

// Width of antd's expand-icon column. The expanded details are padded by the
// same amount so their columns line up with the primary card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  criticalIcon: {
    color: 'red',
    marginRight: 6,
  },
  // The report date can swap into an inline picker, so this line has to wrap.
  dateLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
  },
  // Action links share one left-aligned line; they wrap on narrow screens.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 14,
    marginTop: 4,
  },
  // Same grid, indented to align with the primary card past the expand column.
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

const linkButtonStyle = { paddingLeft: 0 }

// Always-visible summary shown for every office check row on mobile.
export function OfficeCheckMobileCard({
  report,
  canSign = false,
  editDate = undefined,
  warningColor = undefined,
  onDelete,
  onEditDateChange,
  onSaveDate,
  toEditOfficeCheck,
  toOfficeCheck,
}) {
  const classes = useStyles()
  const {
    checkDate,
    critical,
    hasBiobridgeReport,
    hasDeviceReport,
    id,
    isArchived,
    patient,
    signedAt,
  } = report

  // formatDOB(undefined) would render today's date, so only format a real value.
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const detailLine = [dob, patient?.currentDevice?.modelType]
    .filter(Boolean)
    .join(' · ')

  // Mirrors the desktop column: a falsy stored value means "not editing".
  const isEditingDate = Boolean(editDate)
  const showSign = hasBiobridgeReport && canSign && !signedAt && !isArchived
  const falseStyle = warningColor ? { color: warningColor } : undefined

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {critical && (
          <Tooltip title="Requires Attention">
            <ExclamationCircleOutlined className={classes.criticalIcon} />
          </Tooltip>
        )}
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {detailLine && <div className={classes.line}>{detailLine}</div>}
      <div className={`${classes.line} ${classes.dateLine}`}>
        {isEditingDate ? (
          <>
            <DatePicker
              value={dayjs(editDate)}
              onChange={date => onEditDateChange(id, date)}
            />
            <Tooltip title="Save Report Date">
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => onSaveDate(report)}
              />
            </Tooltip>
            <Tooltip title="Cancel">
              <Button
                type="link"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onEditDateChange(id, undefined)}
              />
            </Tooltip>
          </>
        ) : (
          <>
            <span>
              <CalendarOutlined className={classes.lineIcon} />
              {checkDate ? formatDOB(checkDate) : '—'}
            </span>
            <Tooltip title="Edit Report Date">
              <Button
                type="link"
                size="small"
                icon={<EditFilled />}
                onClick={() => onEditDateChange(id, checkDate)}
              />
            </Tooltip>
          </>
        )}
      </div>
      <div className={classes.line}>
        Device report{' '}
        <CircleCheckIcon checked={hasDeviceReport} falseStyle={falseStyle} /> ·
        Completed{' '}
        <CircleCheckIcon checked={hasBiobridgeReport} falseStyle={falseStyle} />{' '}
        · Signed{' '}
        <CircleCheckIcon checked={Boolean(signedAt)} falseStyle={falseStyle} />
      </div>
      <div className={classes.actionsLine}>
        {!hasDeviceReport && (
          <>
            <Link to={toEditOfficeCheck(id)}>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                style={linkButtonStyle}
              >
                Add device report
              </Button>
            </Link>
            <Popconfirm
              title="Are you sure you want to delete this report?"
              onConfirm={() => onDelete(id)}
              okText="Yes"
              cancelText="No"
              placement="bottomRight"
            >
              <Button
                type="link"
                size="small"
                icon={<DeleteFilled />}
                style={linkButtonStyle}
              >
                Delete
              </Button>
            </Popconfirm>
          </>
        )}
        {showSign && (
          <Link to={toOfficeCheck(id)}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              style={linkButtonStyle}
            >
              Sign
            </Button>
          </Link>
        )}
        {hasDeviceReport && (
          <>
            <Link to={toOfficeCheck(id)}>
              <Button
                type="link"
                size="small"
                icon={<FileSearchOutlined />}
                style={linkButtonStyle}
              >
                View
              </Button>
            </Link>
            <Link to={toEditOfficeCheck(id)}>
              <Button
                type="link"
                size="small"
                icon={<EditFilled />}
                style={linkButtonStyle}
              >
                Edit
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

const reportShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  checkDate: PropTypes.string,
  critical: PropTypes.bool,
  hasBiobridgeReport: PropTypes.bool,
  hasDeviceReport: PropTypes.bool,
  isArchived: PropTypes.bool,
  nonBillable: PropTypes.bool,
  signedAt: PropTypes.string,
  patient: PropTypes.shape({
    birthDate: PropTypes.string,
    practiceMrn: PropTypes.string,
    anticoagulatedDisease: PropTypes.bool,
    atrialFibrilationFlutterDisease: PropTypes.bool,
    prnAnticoagulatedDisease: PropTypes.bool,
    currentDevice: PropTypes.shape({
      modelType: PropTypes.string,
      inOfficeCptCode: PropTypes.string,
      inOfficeIcdCode: PropTypes.string,
    }),
  }),
})

OfficeCheckMobileCard.propTypes = {
  report: reportShape.isRequired,
  canSign: PropTypes.bool,
  // The in-progress date, kept by the listing so desktop and mobile share state.
  editDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  warningColor: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onEditDateChange: PropTypes.func.isRequired,
  onSaveDate: PropTypes.func.isRequired,
  toEditOfficeCheck: PropTypes.func.isRequired,
  toOfficeCheck: PropTypes.func.isRequired,
}

// Fields hidden behind the per-row expand toggle.
export function OfficeCheckMobileDetails({ report }) {
  const classes = useStyles()
  const { nonBillable, patient } = report
  const device = patient?.currentDevice

  const hasHistory = Boolean(
    patient?.atrialFibrilationFlutterDisease ||
    patient?.anticoagulatedDisease ||
    patient?.prnAnticoagulatedDisease,
  )

  return (
    <div className={classes.detailsGrid}>
      <Field label="MRN">{patient?.practiceMrn || '—'}</Field>
      {/* Both codes are blank on desktop for non-billable reports. */}
      <Field label="CPT code">
        {(!nonBillable && device?.inOfficeCptCode) || '—'}
      </Field>
      <Field label="ICD-10 code">
        {(!nonBillable && device?.inOfficeIcdCode) || '—'}
      </Field>
      <Field label="Relevant history" span>
        {hasHistory ? <DiseasesTags patientSummary={patient} /> : '—'}
      </Field>
    </div>
  )
}

OfficeCheckMobileDetails.propTypes = {
  report: reportShape.isRequired,
}
